import React from 'react';

import { Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

import { TablePagination } from '../../../components';

import './styles.scoped.css';

const columnClassName = 'align-middle text-nowrap';

// Numeric-aware sort so react-table orders hours/earnings columns by value whether the proc
// returns a string or a number.
const numericSort = (rowA, rowB, columnId) => Number(rowA.values[columnId]) - Number(rowB.values[columnId]);

// Column order/labels per the PRST-120 design (Certified Payroll view).
// Accessors match the exact column names returned by tb_sp_pointsnorth (verified 2026-06-25
// against a data-bearing run: PascalCase, no spaces — EmployeeNumber, PeriodEndDate, RegHours, …).
// Headers are the display labels; accessors are the proc keys. Don't "prettify" the accessors back
// to spaced labels — that renders the columns blank (the CP-801 trap).
export const prevailingWageColumns = [
  { Header: 'Company', accessor: 'Company', className: columnClassName },
  { Header: 'Employee Number', accessor: 'EmployeeNumber', className: columnClassName },
  { Header: 'Employee Name', accessor: 'EmployeeName', className: columnClassName },
  { Header: 'Employee State', accessor: 'EmployeeState', className: columnClassName },
  { Header: 'Period End Date', accessor: 'PeriodEndDate', className: columnClassName },
  { Header: 'Check Date', accessor: 'CheckDate', className: columnClassName },
  { Header: 'Cost Number', accessor: 'CostNumber', className: columnClassName },
  { Header: 'Reg Hours', accessor: 'RegHours', className: columnClassName, sortType: numericSort },
  { Header: 'OT Hours', accessor: 'OTHours', className: columnClassName, sortType: numericSort },
  { Header: 'Reg Earnings', accessor: 'RegEarnings', className: columnClassName, sortType: numericSort },
  { Header: 'Other Earnings', accessor: 'OtherEarnings', className: columnClassName, sortType: numericSort },
  { Header: 'Gross Pay', accessor: 'GrossPay', className: columnClassName, sortType: numericSort },
  { Header: 'Net Pay', accessor: 'NetPay', className: columnClassName, sortType: numericSort },
  { Header: 'Hire Date', accessor: 'HireDate', className: columnClassName },
];

function getSortedIcon(isSorted, isSortedDesc) {
  let iconProp = <FaSort />;
  if (isSorted) {
    iconProp = isSortedDesc ? <FaSortDown /> : <FaSortUp />;
  }
  return iconProp;
}

const PrevailingWageTable = ({ data, loading }) => {
  const tableData = Array.isArray(data) ? data : [];
  const tableInstance = useTable(
    {
      columns: prevailingWageColumns,
      data: tableData,
      // No initial sortBy: tb_sp_pointsnorth already orders detail rows by day with the
      // 7-PAYTOTAL row last (its #PN_Detail Sort column), so preserve the proc's order on load.
      initialState: {
        pageSize: 200,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } = tableInstance;

  const tableHeaders = headerGroups.map(headerGroup => {
    const headers = headerGroup.headers.map(column => (
      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
        {column.render('Header')}
        <span>{column.canSort ? getSortedIcon(column.isSorted, column.isSortedDesc) : null}</span>
      </th>
    ));
    return <tr {...headerGroup.getHeaderGroupProps()}>{headers}</tr>;
  });

  const tableRows = page.map(row => {
    prepareRow(row);
    const cells = row.cells.map(cell => (
      <td
        {...cell.getCellProps({
          className: cell.column.className,
          style: cell.column.style,
        })}>
        {cell.render('Cell')}
      </td>
    ));

    return <tr {...row.getRowProps()}>{cells}</tr>;
  });

  return (
    <Container fluid>
      <Row className="align-items-center">
        <Col className="d-flex justify-content-center justify-content-md-end">
          <TablePagination tableInstance={tableInstance} />
        </Col>
      </Row>
      <Row>
        <Col className="border border-light rounded shadow-sm">
          <Table hover responsive borderless {...getTableProps()}>
            <thead className="text-nowrap">{tableHeaders}</thead>
            <tbody {...getTableBodyProps()}>{tableRows}</tbody>
          </Table>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            {loading && <Spinner animation="border" />}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default PrevailingWageTable;
